/* src/views/jour/JourIncidentsCtrl.jsx — L'ÉCRAN INCIDENTS d'Aujourd'hui
   (lot 3) : le dessin de JourIncidentsProto nourri de listIncidents, le mois
   navigable, les groupes par date ou par logement, les filtres, le bloc
   terracotta « à vous de décider qui paie ». ⚠️ LES FEUILLES D'ARGENT SONT
   CELLES DE L'ACCUEIL (CtrlJugement / CtrlSuivi) — un seul chemin d'écriture,
   jamais un second. L'incident clos s'ouvre en lecture (FeuilleIncidentClos),
   « Revoir la décision » rouvre le jugement prérempli. */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '../../supabase'
import { todayParisIso } from '../MenageTodayView'
import Feuille from '../accueil/Feuille'
import { JourIncidentsProto } from './proto/JourIncidentsProto'
import { FeuilleIncidentClos } from './proto/FeuillesJour'
import { CtrlJugement, CtrlSuivi } from '../accueil/ControleursFeuilles'
import { chargerIncidents } from '../../lib/accueil-api'
import { createIncident } from '../../lib/incidents-api'
import { enqueueIncidentPhoto } from '../../lib/menage-photo-queue'
import { INCIDENT_TYPES } from '../../lib/domain-constants'
import { uid } from '../../lib/format'
import { FeuilleIncidentDeclare } from './proto/FeuillesJour'
import { getDegs } from '../../lib/incidents-helpers'
import { articlesATrancher, aircoverASaisirDeg } from '../../lib/accueil-incident'
import { jourCourt } from '../../lib/accueil-attend'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'

const MOIS_LONGS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']

const prenomDe = (email) => {
  const e = typeof email === 'string' ? email.trim() : ''
  if (!e) return null
  const p = e.split('@')[0]
  return p.charAt(0).toUpperCase() + p.slice(1)
}

export default function JourIncidentsCtrl({ cfg, currentUser, onIncidentsChanged }) {
  const todayIso = useMemo(() => todayParisIso(), [])
  const [incidents, setIncidents] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [mois, setMois] = useState(() => todayParisIso().slice(0, 7))
  const [groupement, setGroupement] = useState('date')
  const [filtre, setFiltre] = useState('tous')
  const [feuille, setFeuille] = useState(null)
  // { jugement: incident } | { suivi: {incident, deg} } | { clos: {incident, deg} }

  const charger = useCallback(() => {
    chargerIncidents(supabase, todayParisIso()).then((r) => {
      if (r.ok) { setIncidents(r.data); setErreur(null) }
      else setErreur(r.motif || 'La lecture des incidents a échoué.')
    })
  }, [])
  useEffect(() => { const id = setTimeout(charger, 0); return () => clearTimeout(id) }, [charger])

  const parLogement = useMemo(() => {
    const m = {}
    for (const l of getAllLogsFlat(cfg)) m[l.id] = l
    return m
  }, [cfg])
  const nomLogement = useCallback((id) => (parLogement[id] && parLogement[id].nom) || id || '—',
    [parLogement])
  const estSousLoc = useCallback((id) => {
    const l = parLogement[id]
    return !!(l && (l.parentLog || l.propId === 'p6' || l.facturationSource === 'v2'))
  }, [parLogement])
  const proprioNomDe = useCallback((logementId) => {
    for (const pp of (cfg && cfg.props) || []) {
      if ((pp.logs || []).some((l) => l.id === logementId
        || (l.chambres || []).some((c) => c.id === logementId))) return pp.nom
    }
    return null
  }, [cfg])

  /* Une LIGNE par dégradation — l'incident est le conteneur, la dégradation
     porte l'état (le modèle réel). */
  const lignesToutes = useMemo(() => {
    const out = []
    for (const i of incidents || []) {
      const degs = getDegs(i) || []
      for (const d of degs) {
        const recuperable = d.statutMenage === 'Récupérable'
        const decide = !!d.etatFinancier
        const aSuivre = aircoverASaisirDeg(d, { sousLoc: estSousLoc(i.logement_id) })
          || (decide && !recuperable
            && ['Achat FULLBNBZEN', 'Refacturé propriétaire', 'AirCover + refacturé']
              .includes(d.etatFinancier) && !d.remplaceLe)
        // « À vérifier » (déclaré par un agent, pas encore jugé) : l'ADMIN
        // JUGE AUSSI (ADR-227 « orchestrateur OU admin », arbitrage 27/08) —
        // le tag « à trancher » dit VRAI, la feuille ouvre le jugement avec
        // son préliminaire récupérable/non.
        const etat = recuperable ? 'recup'
          : d.statutMenage === 'À vérifier' ? 'trancher'
            : !decide && d.statutMenage === 'Non récupérable' ? 'trancher'
              : aSuivre ? 'racheter' : 'regle'
        out.push({
          incident: i, deg: d, etat,
          date: i.incident_date || String(i.reported_at || '').slice(0, 10),
          logement: i.logement_id,
          titre: d.type || 'Article',
          sous: [i.voyageur, nomLogement(i.logement_id),
            prenomDe(i.reported_by) ? `vu par ${prenomDe(i.reported_by)}` : null]
            .filter(Boolean).join(' · '),
        })
      }
    }
    return out.sort((a, b) => String(b.date).localeCompare(String(a.date)))
  }, [incidents, estSousLoc, nomLogement])

  const duMois = useMemo(() => lignesToutes.filter((l) => String(l.date).startsWith(mois)),
    [lignesToutes, mois])
  const filtrees = useMemo(() => duMois.filter((l) => (filtre === 'tous' ? true
    : filtre === 'recup' ? l.etat === 'recup' : l.etat !== 'recup')), [duMois, filtre])

  const ouvrir = useCallback((l) => {
    if (l.deg.statutMenage === 'À vérifier') {
      setFeuille({ jugement: l.incident, degForceRevoir: l.deg }); return
    }
    if (l.etat === 'trancher') setFeuille({ jugement: l.incident })
    else if (l.etat === 'racheter') setFeuille({ suivi: { incident: l.incident, deg: l.deg } })
    else setFeuille({ clos: { incident: l.incident, deg: l.deg } })
  }, [])

  const versLigneProto = (l, prefix) => ({
    etat: l.etat, titre: l.titre, sous: l.sous,
    testId: `${prefix}-${l.incident.id}-${l.deg.id || l.titre}`,
    onOuvrir: () => ouvrir(l),
  })

  const groupes = useMemo(() => {
    const m = new Map()
    for (const l of filtrees) {
      const cle = groupement === 'date' ? l.date : l.logement
      if (!m.has(cle)) m.set(cle, [])
      m.get(cle).push(l)
    }
    return [...m.entries()].map(([cle, ls]) => ({
      titre: groupement === 'date' ? jourCourt(cle).replace(/^le /, '') : nomLogement(cle),
      sous: `${ls.length} incident${ls.length > 1 ? 's' : ''}`,
      lignes: ls.map((l) => versLigneProto(l, 'jour-incident')),
    }))
  }, [filtrees, groupement, nomLogement])   // eslint-disable-line react-hooks/exhaustive-deps

  const aDecider = useMemo(() => {
    const l = duMois.filter((x) => x.etat === 'trancher' || x.etat === 'racheter')
    return l.length === 0 ? null : {
      titre: `${l.length} article${l.length > 1 ? 's' : ''} non récupérable${l.length > 1 ? 's' : ''}`,
      sous: "jugés par l'orchestrateur — à vous de décider qui paie",
      lignes: l.map((x) => versLigneProto(x, 'jour-adecider')),
    }
  }, [duMois])   // eslint-disable-line react-hooks/exhaustive-deps

  const [an, moisN] = mois.split('-').map(Number)
  const moisPrec = () => setMois(moisN === 1 ? `${an - 1}-12` : `${an}-${String(moisN - 1).padStart(2, '0')}`)
  const moisSuiv = () => setMois(moisN === 12 ? `${an + 1}-01` : `${an}-${String(moisN + 1).padStart(2, '0')}`)
  const moisCourant = todayIso.slice(0, 7)

  const decide = useCallback(() => {
    setFeuille(null); charger()
    onIncidentsChanged && onIncidentsChanged()
  }, [charger, onIncidentsChanged])

  // ── DÉCLARER UN INCIDENT (arbitrage Dimitri 27/08) : le bouton principal
  // en tête de l'écran — « c'est là qu'on déclare ce qu'on constate
  // soi-même, sans passer par un agent ». Chemin réel : upload photos
  // (bucket privé, EXIF préservés) puis createIncident(admin_manual).
  // L'admin qui déclare « abîmé » CONSTATE lui-même : la dégradation naît
  // « Non récupérable » (il est juge, ADR-227) et la ligne « à trancher »
  // s'ouvre naturellement sur « qui paie ».
  const [decl, setDecl] = useState(null) // {logement, article, photos, envoi}
  const declarer = useCallback(async () => {
    if (!decl || decl.envoi || !decl.logement || !decl.article
      || (decl.photos || []).length < 2) return
    setDecl((d) => ({ ...d, envoi: true }))
    try {
      await createIncident({
        logement_id: decl.logement,
        source: 'admin_manual',
        degradations: [{ id: uid(), type: decl.article, statutMenage: 'Non récupérable' }],
        photos: decl.photos.map((p) => p.path || p.publicUrl || p),
        incident_date: todayIso,
        reported_by: currentUser && currentUser.email,
        label: decl.article,
      })
      setDecl(null); charger()
      onIncidentsChanged && onIncidentsChanged()
    } catch (e) {
      setDecl((d) => ({ ...d, envoi: false,
        erreur: e?.message || 'La déclaration a été refusée.' }))
    }
  }, [decl, todayIso, currentUser, charger, onIncidentsChanged])

  const ajouterPhoto = useCallback(async (file) => {
    if (!file || !decl || !decl.logement) return
    try {
      const r = await enqueueIncidentPhoto({
        logementId: decl.logement, runId: 'admin-manuel',
        tmpIncidentId: decl.tmpId, file,
      })
      setDecl((d) => ({ ...d, erreur: null, photos: [...(d.photos || []), r] }))
    } catch (e) {
      setDecl((d) => ({ ...d, erreur: `Photo refusée : ${e?.message || e}` }))
    }
  }, [decl])

  return (
    <>
      {incidents === null && !erreur && <div className="page"><p className="note">On lit les incidents…</p></div>}
      {erreur && <div className="page"><p className="note" role="alert"
        style={{ color: '#8f2f1d' }}>{erreur}</p></div>}
      {incidents !== null && (
        <div className="page" style={{ paddingBottom: 0 }}>
          <div className="act" style={{ margin: '0 0 12px' }}>
            <button className="bt bt-p" data-testid="incidents-declarer"
              onClick={() => setDecl({ logement: null, article: null, photos: [],
                tmpId: uid() })}>Déclarer un incident</button>
          </div>
        </div>
      )}
      {incidents !== null && (
        <JourIncidentsProto
          mois={{ label: `${MOIS_LONGS[moisN - 1].charAt(0).toUpperCase()}${MOIS_LONGS[moisN - 1].slice(1)} ${an}`,
            n: duMois.length, onPrec: moisPrec, onSuiv: moisSuiv,
            suivActif: mois < moisCourant }}
          stats={{
            enCours: duMois.filter((l) => l.etat === 'trancher' || l.etat === 'racheter').length,
            regles: duMois.filter((l) => l.etat === 'regle').length,
            recuperes: duMois.filter((l) => l.etat === 'recup').length,
          }}
          aDecider={aDecider}
          groupement={groupement} onGroupe={setGroupement}
          filtres={[
            { id: 'tous', lb: 'Tous', n: duMois.length },
            { id: 'nonrec', lb: 'Non récupérables', n: duMois.filter((l) => l.etat !== 'recup').length },
            { id: 'recup', lb: 'Récupérables', n: duMois.filter((l) => l.etat === 'recup').length },
          ]}
          filtreActif={filtre} onFiltre={setFiltre}
          groupes={groupes}
          note={`${duMois.length === 0 ? 'Aucun incident'
            : duMois.length === 1 ? 'Un incident'
              : `${['Deux', 'Trois', 'Quatre', 'Cinq', 'Six'][duMois.length - 2] || duMois.length} incidents`} en ${MOIS_LONGS[moisN - 1]}. Les mois précédents se retrouvent avec les flèches de mois.`}
        />
      )}

      {decl && (
        <Feuille testId="jour-feuille-declarer" titre="Déclarer un incident"
          onFermer={() => setDecl(null)}
          enfants={
            <>
              <FeuilleIncidentDeclare
                logements={getAllLogsFlat(cfg).map((l) => ({ id: l.id, nom: l.nom }))}
                articles={[...INCIDENT_TYPES]}
                logement={decl.logement}
                onLogement={(id) => setDecl((d) => ({ ...d, logement: id }))}
                article={decl.article}
                onArticle={(a) => setDecl((d) => ({ ...d, article: a }))}
                photos={decl.photos || []}
                photosMin={2}
                onAppareil={() => {
                  const inp = document.getElementById('declarer-photo-input')
                  if (inp) { inp.setAttribute('capture', 'environment'); inp.click() }
                }}
                onGalerie={() => {
                  const inp = document.getElementById('declarer-photo-input')
                  if (inp) { inp.removeAttribute('capture'); inp.click() }
                }}
                pret={!!(decl.logement && decl.article
                  && (decl.photos || []).length >= 2 && !decl.envoi)}
                onEnregistrer={declarer}
                erreur={decl.erreur ? <p role="alert" data-testid="declarer-erreur"
                  style={{ marginTop: 10, fontSize: 12, color: '#8f2f1d' }}>{decl.erreur}</p>
                  : (!decl.logement
                    ? <p className="note">Choisissez d'abord le logement — les photos s'y rangent.</p>
                    : null)} />
              <input id="declarer-photo-input" type="file" accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0]
                  e.target.value = ''
                  if (f) ajouterPhoto(f)
                }} />
            </>
          } />
      )}
      {feuille && feuille.jugement && (
        <CtrlJugement incident={feuille.jugement}
          degForce={feuille.degForceRevoir
            || (articlesATrancher(feuille.jugement)[0] ? null
              : (getDegs(feuille.jugement) || [])[0])}
          sousLoc={estSousLoc(feuille.jugement.logement_id)}
          nomLogement={nomLogement}
          proprioNom={proprioNomDe(feuille.jugement.logement_id)}
          adminEmail={currentUser && currentUser.email}
          onFermer={() => setFeuille(null)}
          onDecide={decide} />
      )}
      {feuille && feuille.suivi && (
        <CtrlSuivi incident={feuille.suivi.incident} deg={feuille.suivi.deg}
          sousLoc={estSousLoc(feuille.suivi.incident.logement_id)}
          nomLogement={nomLogement} todayIso={todayIso}
          echeance={feuille.suivi.incident._echeanceAircover
            ? { phrase: feuille.suivi.incident._echeanceAircover,
                ton: feuille.suivi.incident._echeanceTon || 'neutre' }
            : null}
          onFermer={() => setFeuille(null)}
          onDecide={decide}
          onRevoir={() => setFeuille({ jugement: feuille.suivi.incident,
            degForceRevoir: feuille.suivi.deg })} />
      )}
      {feuille && feuille.clos && (
        <Feuille testId="jour-feuille-incident-clos"
          titre={feuille.clos.deg.type || 'Incident'}
          suffixe={nomLogement(feuille.clos.incident.logement_id)}
          onFermer={() => setFeuille(null)}
          enfants={
            <FeuilleIncidentClos
              recupere={feuille.clos.deg.statutMenage === 'Récupérable'}
              etatTitre={feuille.clos.deg.statutMenage === 'Récupérable' ? 'Récupérable' : 'Réglé'}
              etatSous={[feuille.clos.incident.voyageur,
                feuille.clos.incident.incident_date
                  ? jourCourt(feuille.clos.incident.incident_date) : null]
                .filter(Boolean).join(' · ')}
              etatPhrase={feuille.clos.deg.statutMenage === 'Récupérable'
                ? "L'article est récupérable — un lavage ou une réparation suffit, rien à racheter."
                : feuille.clos.deg.remplaceLe
                  ? "Racheté et redéposé. L'article est revenu au compte du linge."
                  : null}
              lignes={[
                { lb: 'Signalé', v: [prenomDe(feuille.clos.incident.reported_by) ? `par ${prenomDe(feuille.clos.incident.reported_by)}` : "par l'agent",
                  feuille.clos.incident.incident_date ? jourCourt(feuille.clos.incident.incident_date) : null]
                  .filter(Boolean).join(', ') },
                { lb: 'Jugé', v: feuille.clos.deg.statutMenage === 'À vérifier'
                  ? "pas encore — l'orchestrateur doit passer"
                  : feuille.clos.deg.statutMenage === 'Récupérable'
                    ? 'récupérable' : 'non récupérable' },
                ...(feuille.clos.deg.etatFinancier
                  ? [{ lb: 'Décision', v: feuille.clos.deg.etatFinancier }] : []),
                ...(feuille.clos.deg.racheteLe
                  ? [{ lb: 'Racheté', v: jourCourt(feuille.clos.deg.racheteLe) }] : []),
                ...(feuille.clos.deg.remplaceLe
                  ? [{ lb: 'Déposé', v: jourCourt(feuille.clos.deg.remplaceLe) }] : []),
              ]}
              onFermer={() => setFeuille(null)}
              onRevoir={feuille.clos.deg.etatFinancier
                ? () => setFeuille({ jugement: feuille.clos.incident,
                    degForceRevoir: feuille.clos.deg })
                : null} />
          } />
      )}
    </>
  )
}
